import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, PartyPopper } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AGES } from "@/i18n/translations";
import { useLang } from "@/context/LanguageContext";

const empty = {
  prenom: "",
  email: "",
  telephone: "",
  tranche_age: "",
  region: "",
  station_activite: "",
  solo_ou_team: "solo",
  taille_team: "",
  consentement_rgpd: false,
};

const inputCls =
  "border-0 bg-[#F6F1E8] text-[#14283A] placeholder:text-[#14283A]/40 focus-visible:ring-2 focus-visible:ring-[#EA7A4F]";

export default function WaitlistModal({ open, onOpenChange, onSuccess }) {
  const { t } = useLang();
  const m = t.modal;
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => {
    setForm(empty);
    setDone(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consentement_rgpd) {
      toast.error(m.errConsent);
      return;
    }
    if (!form.tranche_age) {
      toast.error(m.errAge);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        taille_team:
          form.solo_ou_team === "team" && form.taille_team ? parseInt(form.taille_team, 10) : null,
      };
      const { data } = await api.post("/waitlist", payload);
      setDone(true);
      onSuccess?.(data.total_inscrits);
      toast.success(m.toastSuccess);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(reset, 250);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-h-[92vh] overflow-y-auto border-2 border-[#EA7A4F] bg-[#14283A] text-[#F6F1E8] sm:max-w-md"
        data-testid="waitlist-modal"
      >
        {done ? (
          <div className="py-8 text-center" data-testid="waitlist-success">
            <div className="grad-bar mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full text-white">
              <PartyPopper className="h-8 w-8" />
            </div>
            <h3 className="font-display text-2xl">{m.successTitle}</h3>
            <p className="mt-3 text-[#F6F1E8]/80">{m.successBody}</p>
            <button
              onClick={() => handleOpenChange(false)}
              className="grad-bar mt-6 rounded-full px-6 py-3 font-bold text-white transition-transform hover:scale-105"
              data-testid="waitlist-close-success"
            >
              {m.close}
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl uppercase tracking-tight">
                {m.title} <span className="grad-text">{m.titleAccent}</span>
              </DialogTitle>
              <p className="text-sm text-[#F6F1E8]/70">{m.subtitle}</p>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-4" data-testid="waitlist-form">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="prenom">{m.prenom}</Label>
                  <Input id="prenom" required value={form.prenom} onChange={(e) => set("prenom", e.target.value)} className={inputCls} data-testid="waitlist-prenom" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="age">{m.age}</Label>
                  <Select value={form.tranche_age} onValueChange={(v) => set("tranche_age", v)}>
                    <SelectTrigger id="age" className={inputCls} data-testid="waitlist-age">
                      <SelectValue placeholder={m.agePlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {AGES.map((a) => (
                        <SelectItem key={a} value={a}>{a} {m.ageSuffix}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email">{m.email}</Label>
                <Input id="email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="waitlist-email" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="tel">{m.tel}</Label>
                  <Input id="tel" required value={form.telephone} onChange={(e) => set("telephone", e.target.value)} className={inputCls} data-testid="waitlist-telephone" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="region">{m.region}</Label>
                  <Input id="region" required value={form.region} onChange={(e) => set("region", e.target.value)} className={inputCls} data-testid="waitlist-region" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="station">{m.station}</Label>
                <Input id="station" placeholder={m.stationPlaceholder} value={form.station_activite} onChange={(e) => set("station_activite", e.target.value)} className={inputCls} data-testid="waitlist-station" />
              </div>

              <div className="space-y-2">
                <Label>{m.comingLabel}</Label>
                <RadioGroup value={form.solo_ou_team} onValueChange={(v) => set("solo_ou_team", v)} className="flex gap-3">
                  {[["solo", m.solo], ["team", m.team]].map(([val, label]) => (
                    <label
                      key={val}
                      htmlFor={`r-${val}`}
                      className={`flex flex-1 cursor-pointer items-center gap-2 rounded-lg border-2 px-3 py-2.5 transition-colors ${
                        form.solo_ou_team === val ? "border-[#EA7A4F] bg-[#EA7A4F]/15" : "border-[#F6F1E8]/25"
                      }`}
                    >
                      <RadioGroupItem id={`r-${val}`} value={val} data-testid={`waitlist-${val}`} />
                      <span className="font-semibold">{label}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              {form.solo_ou_team === "team" && (
                <div className="space-y-1.5">
                  <Label htmlFor="team-size">{m.teamSize}</Label>
                  <Input id="team-size" type="number" min="2" value={form.taille_team} onChange={(e) => set("taille_team", e.target.value)} className={inputCls} data-testid="waitlist-team-size" />
                </div>
              )}

              <label className="flex items-start gap-3 text-sm text-[#F6F1E8]/80">
                <Checkbox
                  checked={form.consentement_rgpd}
                  onCheckedChange={(v) => set("consentement_rgpd", !!v)}
                  className="mt-0.5 border-[#F6F1E8]/50 data-[state=checked]:border-[#EA7A4F] data-[state=checked]:bg-[#EA7A4F]"
                  data-testid="waitlist-consent"
                />
                <span>
                  {m.consentPre}
                  <a href="/confidentialite" target="_blank" rel="noopener noreferrer" className="underline decoration-[#EA7A4F]">
                    {m.consentLink}
                  </a>
                  .
                </span>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="grad-bar flex w-full items-center justify-center gap-2 rounded-full py-3.5 font-bold text-white transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60"
                data-testid="waitlist-submit"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {m.submit}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
